import { useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'
import { useNavigate } from 'react-router-dom'
import { adminService, type EventImportResult } from '../../services/adminService'
import { apiError } from '../errors'
import { useToast } from '../useToast'

function formatBytes(n?: number): string {
  if (n == null) return ''
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`
}

function Progress({ loaded, total, label }: { loaded: number; total?: number; label: string }) {
  const pct = total ? Math.min(100, Math.round((loaded / total) * 100)) : undefined
  return (
    <Box>
      <LinearProgress
        variant={pct == null ? 'indeterminate' : 'determinate'}
        value={pct ?? 0}
        sx={{ borderRadius: 1, height: 6 }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
        {label}
        {loaded > 0 && ` · ${formatBytes(loaded)}${total ? ` of ${formatBytes(total)}` : ''}`}
      </Typography>
    </Box>
  )
}

/** Export the whole event (data + media) as a single .zip bundle. */
export function ExportEventDialog({
  eventId,
  eventName,
  onClose,
}: {
  eventId: string
  eventName: string
  onClose: () => void
}) {
  const { notify } = useToast()
  const [includeAttendees, setIncludeAttendees] = useState(true)
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState<{ loaded: number; total?: number }>({ loaded: 0 })

  const run = async () => {
    setBusy(true)
    setProgress({ loaded: 0 })
    try {
      const { blob, filename } = await adminService.exportEventBundle(eventId, {
        includeAttendees,
        onProgress: (loaded, total) => setProgress({ loaded, total }),
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
      notify('Event exported')
      onClose()
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Export event</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Download <strong>{eventName}</strong> as a single bundle file (.zip) containing all of
            its data and uploaded media — agenda, speakers, content, pages, emails, forms,
            registration, sponsors, engagement, and more. Use <em>Import event</em> in another
            environment to recreate it.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeAttendees}
                onChange={(e) => setIncludeAttendees(e.target.checked)}
                disabled={busy}
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  Include attendees &amp; activity
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Attendees, registrations, form responses, Q&amp;A, polls, meetings, leads,
                  check-ins and engagement. Uncheck for a content-only bundle.
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0.25 } }}
          />
          {includeAttendees && (
            <Alert severity="warning" variant="outlined">
              This bundle will contain attendee personal data. Store and share it securely.
            </Alert>
          )}
          <Alert severity="info" variant="outlined">
            Not included: API keys, webhook endpoints (signing secrets), attendee sign-in links,
            push subscriptions, and queued scheduled sends.
          </Alert>
          {busy && <Progress {...progress} label="Building and downloading bundle…" />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={run} disabled={busy}>
          {busy ? 'Exporting…' : 'Export'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const TABLE_LABELS: [string, string][] = [
  ['sessions', 'sessions'],
  ['speakers', 'speakers'],
  ['content_items', 'content items'],
  ['event_pages', 'pages'],
  ['forms', 'forms'],
  ['sponsors', 'sponsors'],
  ['attendees', 'attendees'],
  ['event_registrations', 'registrations'],
  ['form_responses', 'form responses'],
  ['assets', 'media files'],
]

/** Import a bundle as a brand-new draft event (never overwrites an existing one). */
export function ImportEventDialog({ onClose }: { onClose: () => void }) {
  const { notify } = useToast()
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState<{ loaded: number; total?: number }>({ loaded: 0 })
  const [result, setResult] = useState<EventImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const run = async () => {
    if (!file) return
    setBusy(true)
    setError(null)
    setProgress({ loaded: 0 })
    try {
      const res = await adminService.importEventBundle(file, {
        name: name.trim() || undefined,
        onProgress: (loaded, total) => setProgress({ loaded, total }),
      })
      setResult(res)
      notify('Event imported')
    } catch (e) {
      setError(apiError(e))
    } finally {
      setBusy(false)
    }
  }

  const uploaded = progress.total != null && progress.loaded >= progress.total

  if (result) {
    const summary = TABLE_LABELS.filter(([t]) => (result.counts[t] ?? 0) > 0)
      .map(([t, label]) => `${result.counts[t]} ${label}`)
      .join(' · ')
    return (
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Event imported</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="success">
              <strong>{result.name}</strong> was created as a draft
              {result.slug ? ` (/e/${result.slug})` : ''}.
            </Alert>
            {summary && (
              <Typography variant="body2" color="text.secondary">
                {summary}
              </Typography>
            )}
            {result.warnings.length > 0 && (
              <Alert severity="warning" variant="outlined">
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                  Please review
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {result.warnings.map((w, i) => (
                    <li key={i}>
                      <Typography variant="body2">{w}</Typography>
                    </li>
                  ))}
                </Box>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              onClose()
              navigate(`/admin/events/${result.id}`)
            }}
          >
            Open event
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import event</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Upload an event bundle (.zip) exported from this or another environment. It's created
            as a <strong>new draft event</strong> — existing events are never changed. Scheduled
            campaign and survey sends are not re-armed.
          </Typography>
          <input
            ref={inputRef}
            type="file"
            accept=".zip,application/zip"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null
              setFile(f)
              setError(null)
              e.target.value = ''
            }}
          />
          <Button
            variant="outlined"
            startIcon={<UploadFileRoundedIcon />}
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            sx={{ alignSelf: 'flex-start' }}
          >
            {file ? 'Choose a different file' : 'Choose bundle file'}
          </Button>
          {file && (
            <Typography variant="body2">
              {file.name} <span style={{ opacity: 0.6 }}>({formatBytes(file.size)})</span>
            </Typography>
          )}
          <TextField
            label="Event name (optional)"
            helperText="Leave blank to keep the name from the bundle."
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
          />
          {error && <Alert severity="error">{error}</Alert>}
          {busy && (
            <Progress
              {...progress}
              label={uploaded ? 'Importing data and media…' : 'Uploading bundle…'}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={run} disabled={busy || !file}>
          {busy ? 'Importing…' : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
